<template>
  <div class="scheme-panel">
    <div class="container" :id="`group-${groupIndex}`">
      <div class="group-wrapper">
        <div class="group-top">
          <div class="title-group">
            <button
              class="expand-group"
              @click="collapseGroup"
              :class="{ active: !collapseQuestionGroup }"
            >
              <ArrowExpandGroup />
            </button>

            <button :class="`draggable-icon ${draggable_class}`">
              <GripIcon />
            </button>

            <div>
              <input
                :id="`name-${groupIndex}`"
                type="text"
                @change="rename"
                v-model="groupNameChange"
                :maxlength="60"
              />
            </div>
          </div>

          <span
            v-if="randomTag"
            class="random-tag-badge"
            :style="{ '--badge-bg-color': tagColorMapping[randomTag] }"
            >{{ randomTag }}</span
          >

          <div class="group-btns">
            <div
              class="group-options"
              :id="`group-options-wrapper-${groupIndex}`"
              :ref="`group-options-wrapper-${groupIndex}`"
            >
              <button @click="() => handleGroupOptionsClick(groupIndex)">
                <MenuQuestion />
              </button>

              <div class="menu-dropdown copy-group" :id="`group-options-${groupIndex}`">
                <button class="sort-by-code" @click="() => copyGroup(groupIndex)">
                  <Copy />

                  {{ parseTranslation(COPY_GROUP) }}
                </button>
              </div>
            </div>

            <button class="expand-all" @click="toggleExpandAll()">
              <span v-if="toggleIconExpandAllQuestions"> <Retract /> </span>
              <span v-else>
                <Expand />
              </span>
            </button>

            <button
              class="delete"
              @click="openModalDeleteGroup(groupCode, groupName, questions)"
            >
              <Delete />
            </button>
          </div>
        </div>

        <div class="collapse-group" v-if="collapseQuestionGroup">
          <Draggable
            v-if="questions"
            v-model="questions"
            handle=".handle"
            @start="drag = true"
            @end="drag = false"
          >
            <div
              class="question"
              v-for="[questionIndex, question] in questions.entries()"
              :key="question.qid"
            >
              <div class="question-head">
                <div :id="`menu${questionIndex}-group${groupIndex}`">
                  <button
                    :class="{ handle: isDraggable(question) }"
                    class="menu"
                    @click="handleDrop(questionIndex)"
                    @drag="handleModalOpen(question, modalTypes.drag)"
                  >
                    <MenuQuestion />
                  </button>

                  <div
                    :id="`drop${questionIndex}-group${groupIndex}`"
                    class="menu-dropdown"
                  >
                    <button
                      class="sort-by-code"
                      @click="
                        openModalDeleteAllLogic(questionIndex, (isSortByCode = true))
                      "
                    >
                      <Edit />

                      {{ parseTranslation(SORT_BY_CODE) }}
                    </button>

                    <button
                      class="sort-by-code"
                      @click="
                        openModalDeleteAllLogic(questionIndex, (isSortByCode = false))
                      "
                    >
                      <Edit />
                      {{ parseTranslation(CHANGE_QUESTION_BELOW) }}
                    </button>

                    <button
                      class="copy-question"
                      @click="copyQuestionToSameGroup(question)"
                    >
                      <Copy />
                      {{ parseTranslation(COPY_QUESTION) }}
                    </button>

                    <div class="sub-drop" v-if="availableGroupsToCopyQuestionsTo.length">
                      <button>
                        <Copy />
                        {{ parseTranslation(COPY_QUESTION_IN_DIFF_GROUPS) }}
                      </button>

                      <div class="content">
                        <div v-for="(group, index) in questionGroups" :key="index">
                          <!-- The v-if is being used because we still don't have a control with ids when the ids are implemented in the groups we will update the code -->
                          <button
                            v-if="groupIndex !== index"
                            @click="copyQuestionToDifferentGroup(index, question)"
                          >
                            <Copy />

                            {{ group.group_name }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="sub-drop">
                      <button
                        @click="
                          handleModalOpen(question, modalTypes.moveUp, questionIndex)
                        "
                      >
                        <ArrowUp />
                        {{ parseTranslation(MOVE_UP) }}
                      </button>

                      <div class="content">
                        <button
                          @click="
                            handleModalOpen(question, modalTypes.moveUp, questionIndex)
                          "
                        >
                          <ArrowUp />
                          {{ parseTranslation(MOVE_UP) }}
                        </button>

                        <button
                          @click="
                            handleModalOpen(question, modalTypes.moveStart, questionIndex)
                          "
                        >
                          <ArrowStart />
                          {{ parseTranslation(MOVE_TO_START) }}
                        </button>
                      </div>
                    </div>

                    <div class="sub-drop">
                      <button
                        @click="
                          handleModalOpen(question, modalTypes.moveDown, questionIndex)
                        "
                      >
                        <ArrowDown />
                        {{ parseTranslation(MOVE_DOWN) }}
                      </button>

                      <div class="content">
                        <button
                          @click="
                            handleModalOpen(question, modalTypes.moveDown, questionIndex)
                          "
                        >
                          <ArrowDown />
                          {{ parseTranslation(MOVE_DOWN) }}
                        </button>

                        <button
                          @click="
                            handleModalOpen(question, modalTypes.moveEnd, questionIndex)
                          "
                        >
                          <ArrowEnd />
                          {{ parseTranslation(MOVE_TO_END) }}
                        </button>
                      </div>
                    </div>

                    <button class="add-up" @click="addNew(questionIndex, 'up')">
                      <ArrowUp />

                      {{ parseTranslation(ADD_UP) }}
                    </button>

                    <button class="add-down" @click="addNew(questionIndex + 1, 'down')">
                      <ArrowDown />

                      {{ parseTranslation(ADD_DOWN) }}
                    </button>

                    <button
                      @click="handleModalOpen(question, modalTypes.delete, questionIndex)"
                    >
                      <Delete />

                      {{ parseTranslation(DELETE) }}
                    </button>
                  </div>
                </div>
                <div>
                  <input
                    class="code changeColor"
                    type="text"
                    name="code"
                    :value="question.code"
                    :id="`questionCode${questionIndex}-group${groupIndex}`"
                    :class="{
                      error: questionCodeValidationError[questionIndex],
                      duplicateCodes: duplicateCodes[questionIndex],
                    }"
                    @click="validateQuestionCode(questionIndex)"
                    @keyup="
                      validateCode(
                        questionIndex,
                        `#questionCode${questionIndex}-group${groupIndex}`,
                      )
                    "
                  />

                  <div
                    v-if="
                      question.code === '' || questionCodeValidationError[questionIndex]
                    "
                    class="textCode"
                  >
                    {{
                      parseTranslation(
                        question.code === '' ? ENTER_QUESTION_CODE : QUESTION_CODE_ERROR,
                      )
                    }}
                  </div>

                  <div
                    v-if="questionCodeErrorShownControl[questionIndex]"
                    class="codeChangeMessage"
                  >
                    {{ parseTranslation(VALIDATION_CHANGE_QUESTION_CODE) }}
                  </div>
                </div>

                <textarea
                  class="label"
                  type="text"
                  name="label"
                  :id="`qstText${questionIndex}-group${groupIndex}`"
                  v-model="question.question"
                />

                <select
                  name="type"
                  :value="question.type"
                  @change="e => openTypeChangeModal(e, questionIndex)"
                >
                  <option
                    v-for="[type, displayValue] of Object.entries(typeOptions())"
                    :key="type"
                    :value="type"
                  >
                    {{ displayValue }}
                  </option>
                </select>

                <button
                  :id="`btn-${questionIndex}-group${groupIndex}`"
                  class="expand"
                  @click="toggleQuestionExpandState(questionIndex)"
                >
                  <span v-if="questionExpandControl[questionIndex]"> <Retract /> </span>

                  <span v-else>
                    <Expand />
                  </span>
                </button>
              </div>

              <media-preview
                :mediaId="question.qid"
                :question="question"
                :code="question.code"
                :saveSurvey="saveSurvey"
              />
              <div
                :id="`collapse${questionIndex}-group${groupIndex}`"
                :class="{
                  active: questionExpandControl[questionIndex],
                }"
                class="collapse-container"
              >
                <alternatives
                  v-if="hasSubQuestion(question.type)"
                  :name="answerTypes.subQuestions"
                  :questionIndex="questionIndex"
                  :groupIndex="groupIndex"
                  :expandAll="expandAll"
                  :saveSurvey="saveSurvey"
                />
                <div
                  v-if="
                    hasSubQuestion(question.type) &&
                      !question.subQuestions.length &&
                      !question.advancedOptions.array_filter_reverse &&
                      !question.advancedOptions.array_filter
                  "
                  class="messageAnswer"
                >
                  {{ parseTranslation(LABEL_ADD_SUB_QUESTIONS) }}
                </div>
                <alternatives
                  v-if="hasAnswer(question.type)"
                  :name="answerTypes.answers"
                  :questionIndex="questionIndex"
                  :groupIndex="groupIndex"
                  :expandAll="expandAll"
                  :saveSurvey="saveSurvey"
                />

                <div
                  v-if="
                    hasAnswer(question.type) &&
                      !question.answers.length &&
                      !question.advancedOptions.array_filter_reverse &&
                      !question.advancedOptions.array_filter
                  "
                  class="messageAnswer"
                >
                  {{ parseTranslation(LABEL_ADD_ANSWER) }}
                </div>

                <div>
                  <flow
                    :question_index="questionIndex"
                    :isMessage="isMessage"
                    :groupIndex="groupIndex"
                    :expandAll="expandAll"
                    :deleteConditions="deleteConditions"
                    :disabled="disabledFlow"
                  />
                </div>

                <AdvancedOptions
                  :type="question.type"
                  :question_index="questionIndex"
                  :questions_groups_index="groupIndex"
                  :expandAll="expandAll"
                />

                <div
                  class="validateSliderQuestion"
                  v-if="validateSliderQuestion(questionIndex, question.type)"
                >
                  <label>{{ parseTranslation(VALIDATION_SLIDER_QUESTION) }}</label>
                </div>
              </div>
            </div>
          </Draggable>

          <div class="new-question">
            <button @click="addNew(questions.length, 'end')">
              <span class="plus-sign"> + </span>

              <span> {{ parseTranslation(ADD_QUESTION) }} </span>
            </button>
          </div>

          <group-flow
            v-if="questions.length"
            :group_index="groupIndex"
            :deleteConditions="deleteConditions"
            :expandAll="expandAll"
            :disabled="disabledGroupFlow"
          />

          <group-advanced-options
            :questions_groups_index="groupIndex"
            :expandAll="expandAll"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { v4 as uuidv4 } from 'uuid'
import { mapActions, mapGetters } from 'vuex'

import Alternatives from './Alternatives'
import Flow from './Flow'
import MediaPreview from './MediaPreview.vue'
import AdvancedOptions from './AdvancedOptions'
import GroupFlow from './GroupFlow'
import GroupAdvancedOptions from './GroupAdvancedOptions'

import GripIcon from '@/components/imgs/GripIcon'
import ArrowUp from '@/components/imgs/ArrowUp'
import ArrowDown from '@/components/imgs/ArrowDown'
import ArrowStart from '@/components/imgs/ArrowStart'
import ArrowEnd from '@/components/imgs/ArrowEnd'
import Delete from '@/components/imgs/Delete'
import Copy from '@/components/imgs/Copy'
import Edit from '@/components/imgs/Edit'
import ArrowExpandGroup from '@/components/imgs/ArrowExpandGroup'
import MenuQuestion from '@/components/imgs/MenuQuestion'
import Expand from '@/components/imgs/Expand'
import Retract from '@/components/imgs/Retract'

import { questionsConfig, defaultQuestionCopyExecutor } from '@/utils/questions'
import { ANSWER_TYPES, QUESTION_TYPES } from '@/utils/constants'
import { getLastIndex, getLastItem } from '@/utils/array'

import { deepCopyObject } from '@/utils/object'

import * as translationQuestions from './Questions.translations'

const { default: translations, ...translationKeys } = translationQuestions

export default {
  name: 'Questions',

  props: {
    groupIndex: {
      required: true,
    },
    questionGroups: {
      required: true,
    },
    saveSurvey: {
      required: true,
    },
    draggable_class: {
      required: true,
      type: String,
    },
  },

  components: {
    Alternatives,
    Draggable,
    Flow,
    MediaPreview,
    AdvancedOptions,
    GroupAdvancedOptions,
    GroupFlow,

    // ICONS
    GripIcon,
    ArrowUp,
    ArrowDown,
    ArrowStart,
    ArrowEnd,
    Delete,
    Copy,
    Edit,
    ArrowExpandGroup,
    MenuQuestion,
    Expand,
    Retract,
  },

  data() {
    return {
      translations,
      ...translationKeys,
      answerTypes: ANSWER_TYPES,
      groupName: '',
      groupCode: '',
      groupLogic: {},
      isMessage: false,
      modalTypes: {
        drag: 'drag',
        moveUp: 'moveUp',
        moveStart: 'moveStart',
        moveDown: 'moveDown',
        moveEnd: 'moveEnd',
        delete: 'delete',
      },
      expandAll: false,
      deleteConditions: false,
      qstCondition: null,
      groupNameChange: '',
      collapseQuestionGroup: true,
      toggleIconExpandAllQuestions: false,
      questionExpandControl: {},
      questionCodeValidationError: false,
      duplicateCodes: {},
      isSortByCode: false,
      questionCodeErrorShownControl: {},
    }
  },

  computed: {
    ...mapGetters(['mediaObj', 'deleteMedia', 'survey']),

    questions: {
      get() {
        return this.questionGroups[this.groupIndex].questions
      },
      set(questions) {
        this.updateSpecificQuestionGroup({
          index: this.groupIndex,
          questions,
        })
      },
    },

    randomTag() {
      const code = this.questionGroups[this.groupIndex].group_code

      const tag = this.survey.advancedOptions?.randomize_by_tags?.find?.(({ groups }) =>
        groups.includes(code),
      )

      return tag?.name
    },

    tagColorMapping() {
      const tags = this.survey.advancedOptions?.randomize_by_tags || []

      const possibleColors = [
        '#0695fd',
        '#19c29b',
        '#e33f71',
        '#ff0000',
        '#ff9000',
        '#70cddd',
        '#21262a',
      ]

      const mapping = tags.reduce(
        (acc, next, index) => ({ ...acc, [next.name]: possibleColors[index] || 'blue' }),
        {},
      )

      return mapping
    },

    currentTranslations() {
      return this.translations[this.language]
    },

    survey() {
      return this.$store.getters.survey
    },

    disabledFlow() {
      return this.questionGroups[this.groupIndex]?.advancedOptions?.randomize_questions
    },

    disabledGroupFlow() {
      return ['randomize_groups', 'randomize_by_tags'].some(
        prop => this.survey.advancedOptions?.[prop],
      )
    },

    availableGroupsToCopyQuestionsTo() {
      return this.questionGroups.filter((group, index) => this.groupIndex !== index)
    },
  },

  watch: {
    language() {
      this.typeOptions()
    },

    deleteMedia() {
      if (!this.deleteMedia && this.deleteMedia.answer) return

      const question = this.questions.find(
        question => question.qid === this.deleteMedia.qid,
      )

      if (!question) return

      const indexQuestion = this.questions.indexOf(question)

      this.questions[indexQuestion].media = null
    },

    mediaObj() {
      if (this.mediaObj && !this.mediaObj.answer) {
        const question = this.questions.find(
          question => question.qid === this.mediaObj.qid,
        )
        if (question) {
          const indexQuestion = this.questions.indexOf(question)
          this.questions[indexQuestion].media = {
            width: this.mediaObj.width,
            height: this.mediaObj.height,
            type: this.mediaObj.type,
            url: this.mediaObj.url,
          }
        }
      }
    },
  },
  methods: {
    getId: uuidv4,

    ...mapActions([
      'modalBtns',
      'sendMessage',
      'updateSurvey',
      'preview',
      'updateSpecificQuestionGroup',
      'setGroupNameChange',
      'executeSurveyAPICall',
      'updateQuestionGroup',
      'setQuestionGroups',
      'setQuestionCodeValidation',
    ]),

    toggleQuestionExpandState(index) {
      this.questionExpandControl = {
        ...this.questionExpandControl,
        [index]: !this.questionExpandControl[index],
      }
    },

    createNewQuestionCode(targetGroupIndex, baseCode = '') {
      const COPY_INDICATIVE = 'copy'
      const INITIAL_CODE_NUM = 0

      const { questions } = this.questionGroups[targetGroupIndex]

      const referenceCode = getLastItem(questions || [])?.code

      if (!referenceCode) return `${baseCode}${COPY_INDICATIVE}${INITIAL_CODE_NUM}`

      const endOfStringNumberRegex = /\d+$/

      const [chars] = referenceCode.split(endOfStringNumberRegex)

      const codeNumber = Number(referenceCode.replace(chars, ''))

      const updatedNumber =
        typeof codeNumber === 'number' ? codeNumber + 1 : INITIAL_CODE_NUM

      const createdCode = `${chars}${updatedNumber}`

      return createdCode
    },

    copyQuestionToSameGroup(question) {
      const updatedQuestions = [
        ...this.questions,
        {
          ...deepCopyObject(question),
          qid: this.getId(),
          code: this.createNewQuestionCode(this.groupIndex),
          logic: {
            conditions: [],
            default: 'finish',
          },
        },
      ]

      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: updatedQuestions,
      })
    },

    copyQuestionToDifferentGroup(targetGroupIndex, question) {
      const updatedQuestions = [
        ...this.questionGroups[targetGroupIndex].questions,
        {
          ...deepCopyObject(question),

          qid: this.getId(),
          code: this.createNewQuestionCode(targetGroupIndex, question.code),

          logic: {
            conditions: [],
            default: 'finish',
          },

          advancedOptions: {
            ...question.advancedOptions,
            array_filter: null,
            array_filter_reverse: null,
          },
        },
      ]

      this.updateSpecificQuestionGroup({
        index: targetGroupIndex,
        questions: updatedQuestions,
      })
    },

    rename() {
      this.setGroupNameChange({
        rename: this.groupNameChange,
        index: this.groupIndex,
      })
    },

    openModalDeleteGroup(code, name, questions) {
      const questionsText = questions.length

      this.modalBtns({
        text: this.parseTranslation(this.DELETE_MODAL_MESSAGE, name, questionsText),
        yes: this.deleteGroup,
        params: { code, modalWarning: true },
      })
    },

    async deleteGroup({ code }) {
      const updateGroups = this.questionGroups.filter(group => group.group_code !== code)

      const success = await this.executeSurveyAPICall({
        method: 'PUT',
        path: 'survey',
        payload: {
          survey_id: this.$store.getters.survey.id_,
          questions_groups: updateGroups,
        },
      })

      if (!success) return

      this.setQuestionGroups(updateGroups)
    },

    hasDuplicates(array) {
      const valuesSoFar = []
      for (let i = 0; i < array.length; ++i) {
        const value = array[i]
        if (valuesSoFar.indexOf(value) !== -1) {
          return true
        }
        valuesSoFar.push(value)
      }
      return false
    },

    updateQuestionAdvancedOption({ question, option, value }) {
      return {
        ...deepCopyObject(question),
        advancedOptions: {
          ...question.advancedOptions,
          [option]: value,
        },
      }
    },

    updateAdvancedOptionOnValueMatch({ question, option, compareValue, updateValue }) {
      const existingValue = question.advancedOptions[option]

      if (existingValue !== compareValue) return question

      return this.updateQuestionAdvancedOption({
        question,
        option,
        value: updateValue,
      })
    },

    updateArrayFiltersWithNewCode(oldCode, newCode) {
      if (oldCode === newCode) return this.questions

      return this.questions.map(question => {
        const {
          advancedOptions: { array_filter, array_filter_reverse },
        } = question

        const notApplicable = !array_filter && !array_filter_reverse

        if (notApplicable) return question

        const applicableOption = array_filter ? 'array_filter' : 'array_filter_reverse'

        return this.updateAdvancedOptionOnValueMatch({
          question,
          option: applicableOption,
          compareValue: oldCode,
          updateValue: newCode,
        })
      })
    },

    validateCodeWithNumber(value, index) {
      if (!value) return

      const hasError = !value.match(/^[A-Za-z]/g)

      this.questionCodeValidationError = {
        ...this.questionCodeValidationError,
        [index]: hasError,
      }

      this.setQuestionCodeValidation(hasError)
    },

    deleteSpecificLogic(code) {
      this.questions = this.questions.map((question, index) => ({
        ...question,

        logic: {
          conditions: question.logic.conditions.filter(
            condition => condition.goTo !== code,
          ),

          default: getLastIndex(this.questions) === index ? 'finish' : 'next',
        },
      }))
    },

    validateQuestionCode(index) {
      const hasLogic = this.hasLogicsRelated(this.questions[index].code)

      this.questionCodeErrorShownControl = {
        ...this.questionCodeErrorShownControl,

        [index]: hasLogic,
      }

      const SEVEN_SECONDS = 7000

      setTimeout(() => {
        this.questionCodeErrorShownControl = {
          ...this.questionCodeErrorShownControl,

          [index]: false,
        }
      }, SEVEN_SECONDS)
    },

    duplicateCodeValidation() {
      const questionCodes = this.questions.map(question => question.code)

      const arrayDuplicateCodes = questionCodes.filter(
        (question, index) => questionCodes.indexOf(question) !== index,
      )

      this.questions.forEach((question, index) => {
        this.duplicateCodes = {
          ...this.duplicateCodes,
          [index]: !!arrayDuplicateCodes.find(item => item === question.code),
        }
      })
    },

    validateCode(index, element) {
      let value = document.querySelector(element).value

      value = value.replace(/[\W_]/g, '')

      this.validateCodeWithNumber(value, index)

      this.questions[index].code = value

      this.duplicateCodeValidation()
    },

    isDraggable(question) {
      let resp = true

      if (this.hasLogic(question) || question.logic.conditions.length) resp = false

      return resp
    },

    hasLogic(currentQuestion) {
      let resp = false

      this.questions.forEach(question => {
        question.logic.conditions.forEach(condition => {
          const conditionItem = condition.logic
            .split('AND')
            .join()
            .split('OR')
            .join()
            .split(' , ')
          conditionItem.forEach(item => {
            if (
              currentQuestion.code &&
              currentQuestion.code.toString() === item.split('.')[0].trim()
            )
              resp = true
          })
        })
      })

      return resp
    },

    deleteQstConditions({ qst }) {
      this.deleteConditions = !this.deleteConditions
      this.qstCondition = qst
    },
    getInvalidQuestions(qst) {
      const code = qst.code
      const invalidQuestions = []
      if (qst.logic.conditions.length) invalidQuestions.push(qst.code)
      this.questions.forEach(question => {
        if (question.logic.default === code && !invalidQuestions.includes(question.code))
          invalidQuestions.push(question.code)
        question.logic.conditions.forEach(condition => {
          if (condition.logic.goTo === code && !invalidQuestions.includes(question.code))
            invalidQuestions.push(question.code)
          condition.logic.split(' ').forEach(str => {
            str.split('.').forEach(str2 => {
              if (str2 === code && !invalidQuestions.includes(question.code))
                invalidQuestions.push(question.code)
            })
          })
        })
      })
      let text = ''
      invalidQuestions.forEach((qstCode, index) => {
        if (index === invalidQuestions.length - 1) text += `${qstCode}`
        else text += `${qstCode}, `
      })
      return text
    },

    getModalText(type, question, draggable, logic) {
      if (type === this.modalTypes.delete) {
        return logic
          ? this.parseTranslation(this.MODAL_TEXT_DELETE_WITH_LOGIC, question.code)
          : draggable
          ? this.parseTranslation(
              this.MODAL_TEXT_DELETE,
              this.getInvalidQuestions(question),
            )
          : this.parseTranslation(
              this.MODAL_TEXT_DELETE_WITH_RELATED_LOGIC,
              question.code,
            )
      } else {
        return this.parseTranslation(
          this.MODAL_TEXT_NOT_DELETE,
          this.getInvalidQuestions(question),
        )
      }
    },

    getModalParams(text, question, index) {
      const modalWarning = true

      return {
        [this.modalTypes.drag]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question },
        },

        [this.modalTypes.moveUp]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question, modalWarning },
          next: this.questionStart,
          nextParams: { ind: index },
        },

        [this.modalTypes.moveStart]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question, modalWarning },
          next: this.questionStart,
          nextParams: { ind: index },
        },

        [this.modalTypes.moveDown]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question, modalWarning },
          next: this.questionDown,
          nextParams: { ind: index },
        },

        [this.modalTypes.moveEnd]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question, modalWarning },
          next: this.questionEnd,
          nextParams: { ind: index },
        },

        [this.modalTypes.delete]: {
          text,
          yes: this.deleteQstConditions,
          params: { qst: question, modalWarning },
          next: this.deleteQuestion,
          nextParams: { ind: index },
        },
      }
    },

    getDragHandler: function(index, text) {
      return {
        [this.modalTypes.moveUp]: () => this.questionUp({ index }),
        [this.modalTypes.moveStart]: () => this.questionStart({ index }),
        [this.modalTypes.moveDown]: () => this.questionDown({ index }),
        [this.modalTypes.moveEnd]: () => this.questionEnd({ index }),
        [this.modalTypes.delete]: () =>
          this.modalBtns({
            text,
            yes: this.deleteQuestion,
            params: { ind: index, modalWarning: true },
          }),
      }
    },

    handleModalOpen(question, type, index) {
      const logic = this.hasLogicsRelated(question.code)
      const draggable = this.isDraggable(question)
      const text = this.getModalText(type, question, draggable, logic)
      const types = this.getModalParams(text, question, index)
      const dragTypes = this.getDragHandler(index, text)

      if (type !== this.modalTypes.drag && draggable) {
        dragTypes[type]()
      } else this.modalBtns(types[type])
    },

    openModalDeleteAllLogic(index) {
      const hasLogic = !!this.questions.find(question => question.logic.conditions.length)

      if (!hasLogic) {
        return this.isSortByCode
          ? this.sortByCode({ index })
          : this.changeBelow({ index })
      }

      this.modalBtns({
        text: this.parseTranslation(this.VALIDATION_LOGICS_AND_CONDITIONS),
        yes: this.deleteAllLogics,
        params: { modalWarning: true },
        next: this.isSortByCode ? this.sortByCode : this.changeBelow,
        nextParams: { index },
      })
    },

    deleteAllLogics() {
      this.questions = this.questions.map((question, index) => ({
        ...question,

        logic: {
          conditions: [],
          default: getLastIndex(this.questions) === index ? 'finish' : 'next',
        },
      }))
    },

    hasLogicsRelated(code) {
      return !!this.questions.find(question => {
        return question.logic?.conditions.find(condition => condition.goTo === code)
      })
    },

    hasAnswer(type) {
      return questionsConfig[type].hasAnswer
    },

    hasSubQuestion(type) {
      return questionsConfig[type].hasSubQuestion
    },

    verifyFormulaReference(questionIndex) {
      const { qid } = this.questions[questionIndex]

      const formulaQuestion = this.questions.find(({ type, answers }, index) => {
        if (index < questionIndex || type !== QUESTION_TYPES.formula) return false

        const isReferenced = answers.some(({ conditions }) =>
          (conditions || []).some(({ questionRef }) => questionRef.id === qid),
        )

        return isReferenced
      })

      return formulaQuestion?.code
    },

    openTypeChangeModal(changeEvent, questionIndex) {
      const oldType = this.questions[questionIndex].type
      const newType = changeEvent.target.value

      const deleteList = []

      if (this.hasAnswer(oldType) && !this.hasAnswer(newType)) {
        deleteList.push('answers')
      }

      if (this.hasSubQuestion(oldType) && !this.hasSubQuestion(newType)) {
        deleteList.push('sub-questions')
      }

      const referencesToShow = deleteList.join(' and ')

      const formulaCode = this.verifyFormulaReference(questionIndex)

      if (!referencesToShow && !formulaCode) {
        return this.typeChange({ questionIndex, newType })
      }

      const formulaAdvice = this.parseTranslation(this.ALTERNATIVES_ADVICE, formulaCode)

      const alternativesAdvice = this.parseTranslation(
        this.FORMULA_ADVICE,
        referencesToShow,
      )

      let text = ''

      if (formulaCode) text += formulaAdvice

      if (referencesToShow) text += alternativesAdvice

      text += this.currentTranslations[this.MODAL_ARE_YOU_SURE]

      this.modalBtns({
        text,
        yes: this.typeChange,
        params: { questionIndex, newType },
      })

      /*
       **
       ** This code is to revert HTML's SELECT back to controlled state.
       ** If we do not do this, the @change handler can't prevent the element from
       ** changing it's internal value to what the user selected, even if we select 'NO'
       ** from the confirmation modal. This would cause the select to visually display the
       ** new option, even tho the user canceled the selection due to the warning
       **
       */
      changeEvent.target.value = oldType
    },

    getNewAdvancedOptions({ index, type }) {
      const defaultOptions = questionsConfig[type].advancedOptions
      const acceptedFalsyValue = 0

      return Object.entries(defaultOptions).reduce((options, [option, defaultValue]) => {
        const currentValue = this.questions[index].advancedOptions[option]
        const valueToUpdate =
          currentValue || currentValue === acceptedFalsyValue
            ? currentValue
            : defaultValue

        return {
          ...options,
          [option]: valueToUpdate,
        }
      }, {})
    },

    typeChange({ questionIndex, newType }) {
      const newTypeConfig = questionsConfig[newType]

      this.isMessage = newType === 'message'

      this.questions[questionIndex].type = `${newType}`

      this.questions[questionIndex].advancedOptions = this.getNewAdvancedOptions({
        index: questionIndex,
        type: newType,
      })

      this.transferQuestions({ newType, questionIndex })

      this.clearIncorrectAnswersAndSubQuestions({
        questionIndex,
        type: newType,
      })

      this.questions[questionIndex].lime_type = newTypeConfig.getLimeType(
        this.questions[questionIndex],
      )

      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: this.questions,
      })
    },

    transferQuestions({ newType, questionIndex }) {
      const currentlyHasAnswers = !!this.questions[questionIndex].answers.length
      const currentlyHasSubQuestions = !!this.questions[questionIndex].subQuestions.length

      const hasOnlyAnswers = this.hasAnswer(newType) && !this.hasSubQuestion(newType)

      const hasOnlySubQuestions = !this.hasAnswer(newType) && this.hasSubQuestion(newType)

      const shouldTransferAnswers =
        hasOnlyAnswers && !currentlyHasAnswers && currentlyHasSubQuestions

      const shouldTransferSubquestions =
        hasOnlySubQuestions && currentlyHasAnswers && !currentlyHasSubQuestions

      if (shouldTransferAnswers) {
        this.questions[questionIndex].answers = this.questions[questionIndex].subQuestions
      }

      if (shouldTransferSubquestions) {
        this.questions[questionIndex].subQuestions = this.questions[questionIndex].answers
      }
      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: this.questions,
      })
    },

    clearIncorrectAnswersAndSubQuestions({ questionIndex, type }) {
      if (!this.hasAnswer(type)) this.questions[questionIndex].answers = []

      if (!this.hasSubQuestion(type)) {
        this.questions[questionIndex].subQuestions = []
      }

      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: this.questions,
      })
    },

    addNew(index, where) {
      const hasCodeLetter = []
      const position = index - 1 >= 0 ? index - 1 : 0
      let codeLetterInitial = ''
      const alphabet = []
      let i = 0
      // Create an alphabet from A-Z
      for (i = 65; i < 91; i++) {
        alphabet.push(String.fromCharCode(i))
      }
      // Concatenate the letter 'A' with the alphabet of A-Z
      if (i === 91) {
        let j = 0
        for (j = 65; j < 91; j++) {
          alphabet.push(alphabet[0] + String.fromCharCode(j))
        }
        // Concatenate the letter 'B' with the alphabet of A-Z
        if (j === 91) {
          for (let k = 65; k < 91; k++) {
            alphabet.push(alphabet[1] + String.fromCharCode(k))
          }
        }
      }
      // Adds a letter comparing the survey's index and the 'alphabet' array index. If the survey index is '0' then the letter 'A' will be added as this letter is in the '0' position of the 'alphabet' array
      const CodeSurveyLetter = alphabet.filter((letters, ind) => {
        return this.groupIndex === ind
      })
      // Check if there is a letter that will be added to the groups
      this.questionGroups.forEach(groups => {
        groups.questions.forEach(qst => {
          hasCodeLetter.push(qst.code.replace(/\d/gi, ''))
        })
      })
      // If the letter to be added exists in any group, 'RR' will be added to the code string. Otherwise add the lyrics normally
      if (hasCodeLetter.includes(CodeSurveyLetter[0])) {
        codeLetterInitial = `${alphabet.filter((letters, ind) => {
          return this.groupIndex === ind
        })}RR`
      } else {
        codeLetterInitial = CodeSurveyLetter[0]
      }
      const higherCodeNumber = []
      this.questions.forEach(qst => {
        higherCodeNumber.push(qst.code.replace(/\D/gi, ''))
      })
      if (this.questions.length) {
        let codeLetter = ''
        let codeNumber = ''
        this.questions.forEach(qst => {
          if (
            qst.code.replace(/\d/gi, '') !== this.questions[0].code.replace(/\d/gi, '')
          ) {
            codeLetter = ''
            codeNumber = ''
          } else {
            codeLetter = this.questions[position].code.replace(/\d/gi, '')
            codeNumber = +Math.max(...higherCodeNumber) + 1
          }
        })
        this.questions.splice(index, 0, {
          qid: this.getId(),
          code: `${codeLetter}${codeNumber}`,
          question: 'Question Label',
          answers: [],
          lime_type: 'sft',
          subQuestions: [],
          logic: {
            default: index === this.questions.length ? 'finish' : 'next',
            conditions: [],
          },
          type: 'text',
          advancedOptions: {
            size: 'small',
          },
        })

        if (where !== 'down') {
          this.questions[position].logic.default = 'next'
        }
      } else {
        this.questions.splice(index, 0, {
          qid: this.getId(),
          code: `${codeLetterInitial}0`,
          question: 'Question Label',
          answers: [],
          lime_type: 'sft',
          subQuestions: [],
          logic: {
            default: 'finish',
            conditions: [],
          },
          type: 'text',
          advancedOptions: {
            size: 'small',
          },
        })
      }

      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: this.questions,
      })
    },

    deleteQuestion({ ind }) {
      const questionCode = this.questions[ind].code

      const updatedQuestions = this.questions.filter((_, index) => index !== ind)

      this.questions = updatedQuestions
      // We are updating this logic on our next major update, to remove
      // the strange string and adopt a format similar to our formula reference
      // because of this, we'll keep this as it is, as this code will be completely removed
      // in the next cycle
      updatedQuestions.forEach(question => {
        question.logic.conditions = question.logic.conditions.filter(
          ({ logic }) => !logic.includes(`${questionCode}.`),
        )
      })

      if (this.questions.length && this.questions.length === ind)
        this.questions[ind - 1].logic.default = 'finish'

      this.updateSpecificQuestionGroup({
        index: this.groupIndex,
        questions: this.questions,
      })
    },

    questionUp({ index }) {
      if (index === 0) return

      this.questions.splice(index - 1, 0, this.questions.splice(index, 1)[0])
    },

    questionStart({ index }) {
      if (index === 0) return

      this.questions.splice(0, 0, this.questions.splice(index, 1)[0])
    },

    questionDown({ index }) {
      if (index !== this.questions.length - 1) {
        this.questions.splice(index + 1, 0, this.questions.splice(index, 1)[0])
      }
    },

    questionEnd({ index }) {
      if (index !== this.questions.length - 1) {
        this.questions.splice(
          this.questions.length - 1,
          0,
          this.questions.splice(index, 1)[0],
        )
      }
    },

    sortByCode({ index }) {
      const questionCode = this.questions[index].code.replace(/\d/gi, '')
      let cont = 0
      this.questions.forEach(question => {
        if (question.code.replace(/\d/gi, '') === questionCode)
          question.code = `${questionCode}${cont++}`
      })
    },

    changeBelow({ index }) {
      const baseCode = this.questions[index].code

      let match = /^([a-zA-Z]+)(\d+)$/.exec(baseCode)

      if (match === null) {
        match = /^(\w*)!(\d+)!(\w*)$/.exec(baseCode)
      }

      if (match === null) {
        this.sendMessage(this.currentTranslations[this.CHANGE_BELOW_MESSAGE])
        return
      }

      let [preLetters, number, posLetters] = [match[1], match[2], match[3] || '']

      number = parseInt(number)

      for (
        let questionIndex = index, count = 0;
        questionIndex < this.questions.length;
        questionIndex++, count++
      ) {
        const codeNumber = number + count
        const newCode = `${preLetters}${codeNumber}${posLetters}`
        const oldCode = this.questions[questionIndex].code

        this.questions = this.updateArrayFiltersWithNewCode(oldCode, newCode)

        this.questions[questionIndex].code = newCode
      }
      const inputsCodeQuestions = document.querySelectorAll(`.changeColor`)
      inputsCodeQuestions.forEach(inputCodeQuestion => {
        inputCodeQuestion.style.color = '#696969'
      })
    },

    handleDrop(index) {
      const menuItem = document.querySelector(`#menu${index}-group${this.groupIndex}`)
      const drop = document.querySelector(`#drop${index}-group${this.groupIndex}`)
      drop.classList.toggle('active')
      this.outsideClick(menuItem, ['click'], () => {
        drop.classList.remove('active')
      })
    },

    toggleGroupOptions(groupIndex) {
      const groupOptionsWrapper = this.$refs[`group-options-wrapper-${groupIndex}`]

      const drop = document.querySelector(`#group-options-${groupIndex}`)

      drop.classList.toggle('active')

      this.outsideClick(groupOptionsWrapper, ['click'], () => {
        drop.classList.remove('active')
      })
    },

    handleGroupOptionsClick(groupIndex) {
      const menuItem = document.querySelector(`#group-options-wrapper-${groupIndex}`)

      this.toggleGroupOptions(groupIndex)

      this.outsideClick(menuItem, ['click'], () => {
        drop.classList.remove('active')
      })
    },

    copyAllQuestions(questions) {
      const COPY_INDICATOR = `copy`

      const copyResult = questions.reduce(
        (acc, question) => {
          const { copy, control } = acc

          const newId = this.getId()
          const { qid, type } = question

          const updatedControl = {
            ...control,
            [qid]: newId,
          }

          const copyExecutor =
            questionsConfig[type].copyQuestion || defaultQuestionCopyExecutor

          const copied = copyExecutor({
            question,
            copyIndicator: COPY_INDICATOR,
            idControl: updatedControl,
          })

          return {
            control: updatedControl,
            copy: [...copy, copied],
          }
        },
        { control: {}, copy: [] },
      )

      return copyResult.copy
    },

    copyGroup(groupIndex) {
      const { logic: _, group_name, questions, ...groupConfig } = this.questionGroups[
        groupIndex
      ]

      const copiedGroup = {
        ...groupConfig,
        group_code: this.getId(),
        group_name: `${group_name}_COPY`,
        questions: this.copyAllQuestions(questions),
      }

      this.setQuestionGroups([...this.questionGroups, copiedGroup])

      this.toggleGroupOptions(groupIndex)
    },

    outsideClick(element, events, callback) {
      const html = document.documentElement
      const outside = 'data-outside'

      if (!element.hasAttribute(outside)) {
        events.forEach(userEvent => {
          setTimeout(() => {
            html.addEventListener(userEvent, e => {
              if (!element.contains(event.target)) {
                element.removeAttribute(outside)
                events.forEach(userEvent => {
                  html.removeEventListener(userEvent, this.handleOutsideClick)
                })
                callback()
              }
            })
          })
        })

        element.setAttribute(outside, '')
      }
    },

    collapseGroup() {
      this.collapseQuestionGroup = !this.collapseQuestionGroup
    },

    toggleExpandAll() {
      this.expandAll = !this.expandAll
      this.toggleIconExpandAllQuestions = !this.toggleIconExpandAllQuestions

      const expandAllQuestions = this.questionGroups[this.groupIndex].questions.map(
        (question, index) => ({
          [index]: this.expandAll,
        }),
      )

      this.questionExpandControl = Object.assign({}, ...expandAllQuestions)
    },

    validateSliderQuestion(questionIndex, questionType) {
      const advancedOptions = this.$store.state.questionGroups[this.groupIndex].questions[
        questionIndex
      ].advancedOptions
      return (
        questionType === this.typeOptions().slider.toLowerCase() &&
        ((!advancedOptions.slider_max && advancedOptions.slider_max !== 0) ||
          (!advancedOptions.slider_min && advancedOptions.slider_min !== 0) ||
          (!advancedOptions.slider_accuracy && advancedOptions.slider_accuracy !== 0))
      )
    },

    typeOptions() {
      const options = Object.keys(questionsConfig).reduce(
        (acc, questionType) => ({
          ...acc,
          [questionType]: this.currentTranslations[questionType],
        }),
        {},
      )

      return options
    },
  },

  mounted() {
    window.addEventListener('keydown', this.bindings)
    const group = this.questionGroups[this.groupIndex]
    this.groupName = group.group_name
    this.groupCode = group.group_code
    this.groupLogic = group.logic
    this.groupNameChange = group.group_name
  },
}
</script>

<style lang="scss" scoped>
.container {
  padding: 0 10px;
}

.title-group {
  display: flex;
  align-items: center;
  > div {
    input {
      background: none;
      border: none;
      padding: 6px 5px;
      font-size: 1.5625rem;
      color: $white;
      font-weight: bold;
      height: 43px;
      white-space: nowrap;
      max-width: 320px;
      &:focus,
      &:hover {
        outline: none;
        border: 1px solid $gray-9;
        border-radius: 3px;
      }
    }
  }
  .light & {
    > div {
      input {
        color: $gray-7;
      }
    }
  }
}
.question,
.new-question {
  background: $gray-8;
  border: 2px solid $gray-2;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 3px;
  margin: 20px 0;
  padding: 10px;
  transition: all 1s ease-in-out;
  .light & {
    background: $white;
    border: 2px solid $white;
  }
}
.question-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;

  button {
    border: none;
    background: none;

    &:focus {
      outline: none;
    }
  }

  input,
  textarea {
    display: block;
    background: none;
    border: 1px solid $gray-9;
    border-radius: 3px;
    padding: 6px 5px;
    @include tipography(16);
    flex: 1;
    margin: 0 8px;
    min-height: 35px;
    height: 35px;
    resize: vertical;
    overflow: hidden;
    white-space: pre-wrap;

    &:focus {
      outline: none;
      border: 1px solid $white;
    }

    .light & {
      color: $gray-6;

      &:focus {
        border: 1px solid $gray-8;
      }
    }
  }

  div {
    margin: 0 8px 0 0px;

    input {
      max-width: 60px;
      margin: 0;

      color: $gray-6;

      &.duplicateCodes,
      &.error {
        color: $red;
      }
    }
  }

  select {
    background: linear-gradient(180deg, #3f3f40 0%, #212122 48.96%, #3f3f40 98.96%);
    border: 1px solid $gray-9;
    border-radius: 3px;
    padding: 6px 5px;
    @include tipography(16);
    margin-left: 20px;

    option {
      color: $gray-2;
    }

    &:focus {
      outline: none;
      border: 1px solid $white;
    }

    .light & {
      background: linear-gradient(180deg, #ffffff 0%, #edebeb 48.96%, #ffffff 98.96%);
      color: $gray-6;

      &:focus {
        border: 1px solid $gray-8;
      }
    }
  }

  .textCode,
  .codeChangeMessage {
    position: absolute;
    top: 40px;
    max-width: 350px;
    width: 100%;
    @include tipography(16);
    color: $red;

    animation: show 0.5s forwards;
  }

  .codeChangeMessage {
    @media (max-width: 772px) {
      display: none;
    }
  }
}

.menu {
  cursor: pointer;
  margin-right: 10px;
  display: flex;
  align-items: center;

  svg {
    @include boxSizing(20);
  }
}

.menu-question {
  @include flexMiddle;

  cursor: pointer;
  padding: 0 5px;
}

.draggable-icon {
  @include transparentButton;

  svg {
    @include boxSizing(25);
  }
}

.group-wrapper {
  background: $main-4;
  padding: 30px;
  margin: 30px 0;
  border-radius: 3px;
  .light & {
    background: rgba(0, 0, 0, 0.04);
  }
}

input[type='text'][disabled].light {
  background: rgb(233, 233, 233);
}

.group-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  h3 {
    display: flex;
    align-items: center;
    @include tipography(25);
    color: $white;
    font-weight: bold;
    height: 43px;
  }
  .light & {
    h3 {
      color: $gray-7;
    }
  }
}
.group-btns {
  display: flex;
  align-items: center;

  button {
    border: none;
    background: none;

    &:focus {
      outline: none;
    }
  }

  .expand-all {
    cursor: pointer;
    margin: 0 0 0 11px;
    @include flexAlignCenter;

    span {
      height: 22px;
      svg {
        @include boxSizing(22);
      }
    }
  }

  .delete {
    cursor: pointer;
    margin-left: 20px;
    display: flex;
    align-items: center;

    svg {
      @include boxSizing(25);
    }
  }
}

.remove-question {
  cursor: pointer;
  margin: 0 8px 0 6px;
}

.random-tag-badge {
  background: var(--badge-bg-color);
  padding: 3px 10px;
  color: white;
  border-radius: 20px;
  margin-left: auto;
  display: flex;
  align-items: center;
}

.menu-dropdown {
  width: 265px;
  position: absolute;
  top: 45px;
  z-index: 1;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 2px;
  background: $gray-7;
  display: none;
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;

  &.active {
    display: block;
  }

  &.copy-group {
    width: 140px;
    top: 70px;
  }

  button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 5px 10px;
    @include tipography(11);
    text-transform: uppercase;
    background: none;
    border: none;
    cursor: pointer;

    svg {
      @include boxSizing(18);

      margin-right: 10px;
    }

    &:hover {
      background: $gray-3;
    }

    &:focus {
      outline: none;
    }
  }

  .light & {
    background: $white;

    button {
      color: $gray-2;

      &:hover {
        background: $gray-light-2;
      }
    }
  }
}

.sub-drop {
  position: relative;

  .content {
    max-width: 200px;
    min-width: 150px;
    max-height: 167px;
    position: absolute;
    display: none;
    right: -150px;
    top: 0;
    background: $gray-7;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
    border-radius: 2px;

    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: $gray-4;
      border-radius: 10px;
    }

    div {
      margin: 0;
    }

    button {
      width: 100%;

      &:hover {
        background: $gray-3;
      }

      &:focus {
        outline: none;
      }
    }
  }

  &:hover {
    .content {
      display: block;
    }
  }

  .light & {
    .content {
      background: $white;

      button {
        color: $gray-2;

        &:hover {
          background: $gray-light-2;
        }
      }
    }
  }
}

.new-question {
  button {
    @include transparentButton;
    @include tipography(16);
    color: $blue;

    .plus-sign {
      @include flexMiddle;
      @include boxSizing(20);
      box-sizing: border-box;

      color: $white;
      margin-right: 12px;
      background: $blue;
      font-size: 25px;
      border-radius: 50%;
    }
  }
}

.expand {
  cursor: pointer;
  margin-left: 15px;

  @include transparentButton;

  @include d(s) {
    margin-top: 20px;
  }

  svg {
    @include boxSizing(22);

    transition: all 0.3s ease-in;
  }
}

.expand-group {
  cursor: pointer;
  background: none;
  border: none;
  margin-right: 16px;

  &.focus {
    outline: none;
  }

  svg {
    transition: all 0.4s ease-in;
    @include boxSizing(20);
  }

  &.active {
    svg {
      transform: rotate(180deg);
    }
  }
}

.collapse-container {
  display: none;
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;
  &.active {
    display: block;
  }
}
.collapse-group {
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;
}
.handle {
  cursor: move;
  &:focus {
    cursor: grabbing;
  }
}
.modal {
  position: fixed;
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 auto;
  z-index: 800000;
  animation: show 0.5s forwards;
  > div {
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    width: 300px;
    padding: 25px;
    background: #fff;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
    @include tipography(16);
    color: $main-two;
    div {
      margin-top: 20px;
      button {
        border: none;
        border-radius: 3px;
        padding: 10px 20px;
        letter-spacing: 3px;
        text-transform: uppercase;
        font-weight: bold;
        cursor: pointer;
        @include tipography(15);
        &:focus {
          outline: none;
        }
        &:nth-last-of-type(1) {
          background: $blue;
          margin-left: 15px;
        }
        &:nth-last-of-type(2) {
          color: $gray-2;
          background: $gray-light;
        }
      }
    }
  }
}
@keyframes show {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.validateSliderQuestion {
  margin: 10px 0 0 10px;
  label {
    @include tipography(16);
    color: red;
    font-size: 15px;
  }
}

@media (max-width: 575.98px) {
  .title-group {
    h3 {
      @include tipography(19);
      height: auto;
    }
    > div {
      input {
        font-size: 1.25rem;
      }
    }
    .expand-group {
      margin-left: 0px;
    }
  }
  .group-btns {
    flex-direction: column;
    justify-content: flex-end;

    .expand-all {
      margin: 0 0 10px 0;
    }

    .delete {
      margin-left: 6px;
    }
  }

  .expand {
    cursor: pointer;
    margin-left: auto;
    @include d(s) {
      margin-top: 20px;
    }
  }
}

@media (max-width: 676px) {
  .question-head {
    select {
      margin-top: 20px;
    }
  }
}

.messageAnswer {
  padding: 0;
  border: 0;
  display: flex;
  padding-left: 30px;
  margin: 10px 0;
  font-family: 'PT Sans', 'sans-serif', monospace;
  font-size: 1em;
  color: #0695fd;
}

.group-options {
  margin-right: 12px;

  > button {
    display: flex;
    align-items: center;
  }

  svg {
    width: 25px;
    height: 25px;
  }
}
</style>
